import {
	CloseOutlined,
	DownOutlined,
	PaperClipOutlined,
} from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { Divider } from "components/Divider/Divider"
import { Dropdown } from "components/Dropdown/Dropdown"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { ImageThumbnail } from "components/Image/ImageThumbnail"
import { Input } from "components/Input/Input"
import { type MenuItemType } from "components/Menu/Menu"
import { Space } from "components/Space/Space"
import { Tag } from "components/Tag/Tag"
import { Upload, type UploadProps } from "components/Upload/Upload"
import { useDisclosure } from "hooks/useDisclosure"
import { MessagePayload, MessageType } from "interface/communications"
import { useFlag } from "modules/flags/flags"
import {
	ComponentProps,
	useCallback,
	useEffect,
	useMemo,
	useReducer,
} from "react"
import { useSendMessage } from "services/communications"
import { cn } from "styles/cn"
import { CONSTRAINTS } from "utils/file"
import { useChat } from "./ChatProvider"

const { email, inApp, message } = MessageType

export function ChatInput({
	className,
	disabled = false,
	placeholder = "Write your message...",
	...props
}: ComponentProps<"footer"> & {
	disabled?: boolean
	placeholder?: string
}) {
	const { chatMessageEmail, chatMessageInApp } = useFlag(
		"chatMessageEmail",
		"chatMessageInApp",
	)
	const { sender, patient, activeParticipant, parentMessage, replyTo } =
		useChat()
	const defaultType = useMemo(() => {
		if (chatMessageInApp.enabled && sender.isPatient) {
			return inApp
		}
		if (
			chatMessageEmail.enabled &&
			!activeParticipant.supportsTextMessages &&
			!!activeParticipant.hasEmailAddress
		) {
			return email
		}

		return message
	}, [
		activeParticipant.hasEmailAddress,
		activeParticipant.supportsTextMessages,
		chatMessageEmail.enabled,
		chatMessageInApp.enabled,
		sender.isPatient,
	])
	const [{ type, text, file, subject, inReplyTo, threadId }, dispatch] =
		useReducer(
			(state: MessagePayload, action: Partial<MessagePayload>) => {
				return { ...state, ...action }
			},
			{
				type: defaultType,
				inReplyTo: undefined,
			},
		)
	const imageError = useDisclosure<string>()
	const availableChannels = useMemo<MenuItemType[]>(() => {
		const channels = []
		if (activeParticipant.supportsTextMessages) {
			channels.push({ key: message, label: "Text message" })
		}
		if (chatMessageEmail.enabled && !!activeParticipant.hasEmailAddress) {
			channels.push({ key: email, label: "Email" })
		}
		if (chatMessageInApp.enabled && activeParticipant.contactType === "User") {
			channels.push({ key: inApp, label: "Chat message" })
		}

		return channels
	}, [
		activeParticipant.contactType,
		activeParticipant.hasEmailAddress,
		activeParticipant.supportsTextMessages,
		chatMessageEmail.enabled,
		chatMessageInApp.enabled,
	])
	const hasMultipleChannels = availableChannels.length > 1 && !sender.isPatient
	const isEmail = type === email
	const typeLabel = `Send ${String(availableChannels.find(({ key }) => key === type)?.label).toLowerCase()}`
	const isInvalid = useMemo(() => {
		if (isEmail) {
			return (!text && !file) || !subject
		}
		return !text && !file
	}, [file, isEmail, subject, text])
	const parentMessageSubject = useMemo(() => {
		if (!parentMessage) {
			return undefined
		}
		const addReplyPrefix = !parentMessage.subject?.includes("RE:")
		return `${addReplyPrefix ? "RE: " : ""}${parentMessage.subject}`
	}, [parentMessage])
	const sendMessage = useSendMessage({
		sender,
		patientId: patient.id,
		receiver: activeParticipant.id,
		toType: activeParticipant.id === patient.id ? "User" : "AlternateContact",
	})
	const handleFileChange: UploadProps["onChange"] = (param) => {
		const fileToUpload = param.file as unknown as File
		if (!CONSTRAINTS.ALLOWED_MIME_TYPES.includes(fileToUpload.type)) {
			imageError.open(
				`The supported file types are ${CONSTRAINTS.ALLOWED_MIME_TYPES_WKN.join(", ")}`,
			)
			dispatch({ file: null })
		} else if (fileToUpload.size > CONSTRAINTS.MAX_FILE_SIZE) {
			imageError.open(CONSTRAINTS.MAX_SIZE_ERROR_MESSAGE)
			dispatch({ file: null })
		} else {
			imageError.close()
			dispatch({ file: fileToUpload })
		}
	}
	const handleSend = useCallback(() => {
		if (isInvalid) {
			return
		}
		sendMessage.mutate({
			type,
			file,
			text: (text ?? "").length > 0 ? text : undefined,
			subject: (subject ?? "").length > 0 ? subject : undefined,
			inReplyTo,
			threadId,
		})
		dispatch({ text: "", file: null, subject: "", inReplyTo: undefined })
		replyTo(null)
	}, [
		file,
		inReplyTo,
		isInvalid,
		replyTo,
		sendMessage,
		subject,
		text,
		threadId,
		type,
	])
	const pressEnterHandler = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === "Enter") {
				if (e.shiftKey || e.altKey) {
					e.preventDefault()
					dispatch({ text: text + "\n" })
				} else {
					e.preventDefault()
					handleSend()
				}
			}
		},
		[handleSend, text],
	)
	const handleCloseReply = useCallback(() => {
		replyTo(null)
		dispatch({ inReplyTo: undefined, subject: "" })
	}, [replyTo])

	useEffect(() => {
		if (parentMessage && parentMessageSubject) {
			dispatch({
				type: email,
				inReplyTo: parentMessage.id,
				threadId: parentMessage.threadId,
				subject: parentMessageSubject,
			})
		}
	}, [parentMessage, parentMessageSubject])

	useEffect(() => {
		if (type === email && !availableChannels.some(({ key }) => key === email)) {
			dispatch({
				type: message,
			})
		}
	}, [availableChannels, type])

	return (
		<footer
			{...props}
			className={cn(
				"m-3 space-y-2.5 rounded-lg border border-grey-20 bg-white p-3 transition-all duration-300 hover:border-green-20 has-focus:border-black",
				disabled && "bg-grey-20",
				className,
			)}
		>
			{isEmail && (
				<>
					<Input
						autoFocus
						name="subject"
						value={subject}
						placeholder="Email subject"
						disabled={disabled}
						className="rounded-none border-none p-0"
						onChange={({ target: { value = "" } }) =>
							dispatch({ subject: value.length > 0 ? value : undefined })
						}
						prefix={inReplyTo && <ReplyTag onClose={handleCloseReply} />}
					/>
					<Divider className="mb-2.5" />
				</>
			)}
			<Input.TextArea
				key={file?.lastModified}
				size="large"
				disabled={disabled}
				readOnly={sendMessage.isPending}
				maxLength={737}
				name="text"
				value={text}
				placeholder={placeholder}
				autoSize={{ minRows: 1, maxRows: 10 }}
				className="rounded-none border-none p-0"
				onPressEnter={pressEnterHandler}
				onChange={({ target: { value = "" } }) =>
					dispatch({ text: value.length > 0 ? value : undefined })
				}
			/>
			<div className="flex w-full justify-between gap-1">
				<div className="flex items-end gap-1">
					<Upload
						data-testid="Select an image"
						maxCount={1}
						name="files"
						accept="image/*"
						showUploadList={false}
						beforeUpload={() => false}
						disabled={disabled || sendMessage.isPending}
						onChange={handleFileChange}
					>
						<Button
							size="large"
							aria-label="Select an image"
							className="select-none"
							disabled={disabled || sendMessage.isPending}
							icon={<PaperClipOutlined aria-hidden />}
						/>
					</Upload>
					{file && (
						<ImageThumbnail
							disabled={sendMessage.isPending}
							file={file}
							onRemove={() => dispatch({ file: null })}
						/>
					)}
					<ErrorStatus show={imageError.isOpen}>{imageError.value}</ErrorStatus>
				</div>

				{!hasMultipleChannels && (
					<Button
						type="primary"
						size="large"
						disabled={disabled || isInvalid}
						loading={sendMessage.isPending}
						onClick={handleSend}
					>
						Send
					</Button>
				)}

				{hasMultipleChannels && (
					<Space.Compact>
						<Button
							type="primary"
							size="large"
							disabled={disabled || isInvalid}
							onClick={(e) => {
								e.stopPropagation()
								handleSend()
							}}
						>
							{typeLabel}
						</Button>
						<Dropdown
							trigger={["click"]}
							menu={{
								"aria-label": "Available message channels",
								items: availableChannels,
								selectable: true,
								defaultSelectedKeys: [type as string],
								onClick: ({ key }) => {
									const newType = key as MessageType
									const newSubject =
										newType === MessageType.email ? subject : undefined
									dispatch({ type: newType, subject: newSubject })
								},
								onKeyDown: (e) => e.stopPropagation(),
							}}
						>
							<Button
								type="primary"
								size="large"
								loading={sendMessage.isPending}
								disabled={disabled || isInvalid}
								icon={<DownOutlined aria-hidden />}
								aria-label="Select a message channel"
							/>
						</Dropdown>
					</Space.Compact>
				)}
			</div>
		</footer>
	)
}

function ReplyTag({ onClose }: { onClose: VoidFunction }) {
	return (
		<Tag
			closable
			onClose={onClose}
			closeIcon={
				<CloseOutlined
					tabIndex={0}
					aria-label="Close reply"
					className="focus-outline rounded-full"
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							onClose()
						}
					}}
				/>
			}
		>
			Reply
		</Tag>
	)
}
